/**
   * ------------------------------------------------------------
   * COMPUTE navlog outputs per row
   * 
   * memoize the computed navlog legs so that they are only recalculated
   * when the input values (navlogRows and forecastTerminals) change.
   * 
   * note: map creates a new array
   * for each row in the navlog, it finds the corresponding forecast terminal data
   *  ------------------------------------------------------------
   */

import {
  deg2rad,
  rad2deg,
  norm180,
  normalizeFt,
  interpolation,
  tasFromCas,
} from "../utils/wcaMath";

export function computeWcaLegs(wcaRows, forecastTerminals, altitudes) {
  return wcaRows.map((row) => {
    const ftData = forecastTerminals.find(
      (f) => normalizeFt(f.ft) === normalizeFt(row.ft)
    );

    const cruiseAltitude = Number(row.cruiseAlt);
    const CAS = Number(row.cas);
    const TC = Number(row.tc);

    const rowOk =
      Number.isFinite(cruiseAltitude) &&
      Number.isFinite(CAS) &&
      Number.isFinite(TC) &&
      CAS > 0;

    if (!ftData || !rowOk) {
      return blankComputedRow(row);
    }

    const A1 = Number(altitudes?.alt1 ?? 3000);
    const A2 = Number(altitudes?.alt2 ?? 6000);
    const A3 = Number(altitudes?.alt3 ?? 9000);

    const WD1 = Number(ftData.wd1);
    const WD2 = Number(ftData.wd2);
    const WD3 = Number(ftData.wd3);

    const WS1 = Number(ftData.ws1);
    const WS2 = Number(ftData.ws2);
    const WS3 = Number(ftData.ws3);

    const T1 = ftData.t1 === "" ? null : Number(ftData.t1);
    const T2 = Number(ftData.t2);
    const T3 = Number(ftData.t3);

    const requiredWinds = [
      A1,
      A2,
      A3,
      WD1,
      WD2,
      WD3,
      WS1,
      WS2,
      WS3,
    ];

    const windsOk = requiredWinds.every(Number.isFinite);

    if (!windsOk) {
      return blankComputedRow(row);
    }

    const TAS = tasFromCas(CAS, cruiseAltitude);

    const windDirInterp =
      cruiseAltitude <= A1
        ? WD1
        : cruiseAltitude >= A3
        ? WD3
        : interpolation(cruiseAltitude, A1, A2, A3, WD1, WD2, WD3);

    const windSpeedInterp =
      cruiseAltitude <= A1
        ? WS1
        : cruiseAltitude >= A3
        ? WS3
        : interpolation(cruiseAltitude, A1, A2, A3, WS1, WS2, WS3);

    const isaTemp = 15 - (2 * cruiseAltitude) / 1000.0;

    // For altitudes at or below 3000 ft, use ISA temperature. 
    // Above 3000 ft, use the interpolated temperature.
   const tempInterp =
      cruiseAltitude <= 3000
        ? isaTemp
        : cruiseAltitude <= A1
        ? T1
        : cruiseAltitude >= A3
        ? T3
        : interpolation(cruiseAltitude, A1, A2, A3, T1, T2, T3);

    // Calculate the angle between the wind direction and the true course
    const windAngleDeg = windDirInterp - TC;
    const angle = norm180(windAngleDeg);
    const radians = deg2rad(angle);

    // Headwind and Crosswind components
    const HW = windSpeedInterp * Math.cos(radians);
    const CW = windSpeedInterp * Math.sin(radians);

    // Wind Correction Angle (WCA) calculation
    const ratio = CW / TAS;
    const wcaDeg = Math.abs(ratio) > 1 ? "" : rad2deg(Math.asin(ratio));

    // True Heading (TH) calculation
    const TH = wcaDeg === "" ? "" : TC + wcaDeg;

    return {
      ...row,
      tas: TAS,
      windDir: windDirInterp,
      windSpd: windSpeedInterp,
      tempC: tempInterp,
      wca: wcaDeg,
      th: TH,
      hw: HW,
      cw: CW,
      hwcw: `${Number.isFinite(HW) ? HW.toFixed(0) : ""}/${
        Number.isFinite(CW) ? CW.toFixed(0) : ""
      }`,
    };
  });
}

function blankComputedRow(row) {
  return {
    ...row,
    tas: "",
    windDir: "",
    windSpd: "",
    tempC: "",
    wca: "",
    th: "",
    hw: "",
    cw: "",
    hwcw: "",
  };
}